'use client';

const CHAT_RSA_ALG_NAME = 'RSA-OAEP';
const CHAT_RSA_HASH = 'SHA-256';
const CHAT_AES_ALG_NAME = 'AES-GCM';
const CHAT_GCM_NONCE_BYTES = 12;
const CHAT_AES_KEY_BYTES = 32;

export const CHAT_PUBLIC_KEY_ALG = 'RSA-OAEP-SHA256';
export const CHAT_MESSAGE_ALG = 'AES-GCM-256';
export const CHAT_MESSAGE_KEY_ALG = 'RSA-OAEP-SHA256';

export interface ChatKeyUnsealPayload {
  WrappedPrivateKey?: string;
  WrappedPrivateKeyNonce?: string;
  EncryptedSessionKey?: string;
}

export interface ChatEncryptMessageResult {
  Ciphertext: string;
  CiphertextNonce: string;
  CiphertextAlg: string;
  SenderEncryptedMessageKey: string;
  ReceiverEncryptedMessageKey: string;
  MessageKeyAlg: string;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const ensureChatCryptoAvailable = () => {
  if (!globalThis.crypto?.subtle) {
    throw new Error('WebCrypto is unavailable');
  }
};

// 后端下发的 PEM、公钥信封和消息信封都使用 base64 文本，前端统一在这里做编解码。
const normalizeBase64 = (value: string) => value.replace(/\s+/g, '');

const bytesToBase64 = (value: ArrayBuffer | Uint8Array) => {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return globalThis.btoa(binary);
};

const base64ToBytes = (value: string) => {
  const binary = globalThis.atob(normalizeBase64(value));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const pemBodyToBytes = (pem: string) =>
  base64ToBytes(
    pem
      .replace(/-----BEGIN [^-]+-----/g, '')
      .replace(/-----END [^-]+-----/g, '')
      .replace(/\s+/g, '')
  );

const wrapPem = (label: string, value: ArrayBuffer) => {
  const base64 = bytesToBase64(value);
  const chunks = base64.match(/.{1,64}/g) || [];
  return `-----BEGIN ${label}-----\n${chunks.join('\n')}\n-----END ${label}-----`;
};

const importRsaPublicKey = async (publicKeyPem: string) => {
  ensureChatCryptoAvailable();
  return globalThis.crypto.subtle.importKey(
    'spki',
    pemBodyToBytes(publicKeyPem),
    {
      name: CHAT_RSA_ALG_NAME,
      hash: CHAT_RSA_HASH,
    },
    false,
    ['encrypt']
  );
};

const importRsaPrivateKey = async (privateKeyPem: string) => {
  ensureChatCryptoAvailable();
  return globalThis.crypto.subtle.importKey(
    'pkcs8',
    pemBodyToBytes(privateKeyPem),
    {
      name: CHAT_RSA_ALG_NAME,
      hash: CHAT_RSA_HASH,
    },
    false,
    ['decrypt']
  );
};

const importAesKey = async (
  value: ArrayBuffer,
  usages: KeyUsage[] = ['encrypt', 'decrypt']
) => {
  ensureChatCryptoAvailable();
  return globalThis.crypto.subtle.importKey(
    'raw',
    value,
    {
      name: CHAT_AES_ALG_NAME,
    },
    false,
    usages
  );
};

export const generateChatSessionKeyPair = async () => {
  ensureChatCryptoAvailable();
  // 每次进入消息页都生成浏览器临时 RSA 密钥，只用于本次私钥解封，不写入服务端。
  const keyPair = await globalThis.crypto.subtle.generateKey(
    {
      name: CHAT_RSA_ALG_NAME,
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: CHAT_RSA_HASH,
    },
    true,
    ['encrypt', 'decrypt']
  );
  return {
    publicKeyPem: wrapPem(
      'PUBLIC KEY',
      await globalThis.crypto.subtle.exportKey('spki', keyPair.publicKey)
    ),
    privateKey: keyPair.privateKey,
  };
};

export const unsealChatPrivateKey = async (
  payload: ChatKeyUnsealPayload,
  sessionPrivateKey: CryptoKey
) => {
  ensureChatCryptoAvailable();
  // 服务端返回的是“浏览器临时公钥包裹的 session key + session key 包裹的真实私钥”。
  const sessionKeyBytes = await globalThis.crypto.subtle.decrypt(
    { name: CHAT_RSA_ALG_NAME },
    sessionPrivateKey,
    base64ToBytes(payload.EncryptedSessionKey || '')
  );
  const sessionKey = await importAesKey(sessionKeyBytes, ['decrypt']);
  const privateKeyPemBuffer = await globalThis.crypto.subtle.decrypt(
    {
      name: CHAT_AES_ALG_NAME,
      iv: base64ToBytes(payload.WrappedPrivateKeyNonce || ''),
    },
    sessionKey,
    base64ToBytes(payload.WrappedPrivateKey || '')
  );
  return importRsaPrivateKey(decoder.decode(privateKeyPemBuffer));
};

export const decryptChatMessage = async (
  privateKey: CryptoKey,
  encryptedMessageKey: string,
  ciphertext: string,
  ciphertextNonce: string
) => {
  ensureChatCryptoAvailable();
  // 先解出消息级 AES key，再解正文密文；明文只留在当前浏览器内存或本地缓存。
  const rawMessageKey = await globalThis.crypto.subtle.decrypt(
    { name: CHAT_RSA_ALG_NAME },
    privateKey,
    base64ToBytes(encryptedMessageKey)
  );
  const messageKey = await importAesKey(rawMessageKey, ['decrypt']);
  const plaintext = await globalThis.crypto.subtle.decrypt(
    {
      name: CHAT_AES_ALG_NAME,
      iv: base64ToBytes(ciphertextNonce),
    },
    messageKey,
    base64ToBytes(ciphertext)
  );
  return decoder.decode(plaintext);
};

export const encryptChatMessage = async (
  plaintext: string,
  senderPublicKeyPem: string,
  receiverPublicKeyPem: string
): Promise<ChatEncryptMessageResult> => {
  ensureChatCryptoAvailable();
  // 每条消息单独生成一次 AES key，分别给发送方和接收方包裹信封，便于双方各自解密。
  const messageKey = await globalThis.crypto.subtle.generateKey(
    {
      name: CHAT_AES_ALG_NAME,
      length: CHAT_AES_KEY_BYTES * 8,
    },
    true,
    ['encrypt', 'decrypt']
  );
  const nonce = globalThis.crypto.getRandomValues(
    new Uint8Array(CHAT_GCM_NONCE_BYTES)
  );
  const ciphertext = await globalThis.crypto.subtle.encrypt(
    {
      name: CHAT_AES_ALG_NAME,
      iv: nonce,
    },
    messageKey,
    encoder.encode(plaintext)
  );
  const rawMessageKey = await globalThis.crypto.subtle.exportKey(
    'raw',
    messageKey
  );
  const senderPublicKey = await importRsaPublicKey(senderPublicKeyPem);
  const receiverPublicKey = await importRsaPublicKey(receiverPublicKeyPem);
  const senderEncryptedMessageKey = await globalThis.crypto.subtle.encrypt(
    { name: CHAT_RSA_ALG_NAME },
    senderPublicKey,
    rawMessageKey
  );
  const receiverEncryptedMessageKey = await globalThis.crypto.subtle.encrypt(
    { name: CHAT_RSA_ALG_NAME },
    receiverPublicKey,
    rawMessageKey
  );
  return {
    Ciphertext: bytesToBase64(ciphertext),
    CiphertextNonce: bytesToBase64(nonce),
    CiphertextAlg: CHAT_MESSAGE_ALG,
    SenderEncryptedMessageKey: bytesToBase64(senderEncryptedMessageKey),
    ReceiverEncryptedMessageKey: bytesToBase64(receiverEncryptedMessageKey),
    MessageKeyAlg: CHAT_MESSAGE_KEY_ALG,
  };
};
